import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { playSound, speak } from '../utils/sounds';

const NameInput = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('initial'); // initial, listening, confirming, confirmed
  const [recognizedName, setRecognizedName] = useState('');
  const [isListening, setIsListening] = useState(false);
  const hasInitializedRef = React.useRef(false);
  const stepRef = React.useRef('initial');
  const recognizedNameRef = React.useRef('');
  const isListeningRef = React.useRef(false);
  const recognitionRef = React.useRef(null);

  useEffect(() => {
    // Éviter les doubles appels en mode strict
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    // Vérifier si on a déjà un prénom enregistré
    const savedName = localStorage.getItem('childName');
    if (savedName) {
      navigate('/subjects');
      return;
    }

    // Initialiser la reconnaissance vocale
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recog = new SpeechRecognition();
      recog.lang = 'fr-FR';
      recog.continuous = false;
      recog.interimResults = false;
      recog.maxAlternatives = 1;

      console.log('🎤 Speech Recognition initialized:', {
        userAgent: navigator.userAgent,
        isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
        hasWebkit: 'webkitSpeechRecognition' in window,
        hasStandard: 'SpeechRecognition' in window
      });

      recog.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        const name = transcript.trim();
        console.log('Name recognized:', name, 'Current step:', stepRef.current);

        if (stepRef.current === 'listening') {
          setRecognizedName(name);
          recognizedNameRef.current = name;
          setIsListening(false);
          isListeningRef.current = false;
          setStep('confirming');
          stepRef.current = 'confirming';
          // Répéter le prénom entendu et demander confirmation
          setTimeout(() => {
            speak(`J'ai entendu ${name}. C'est bien ça ? Dis oui ou non.`);
            setTimeout(() => {
              console.log('🔄 Attempting to restart listening for confirmation...');
              startListening();
            }, 3500);
          }, 500);
        } else if (stepRef.current === 'confirming') {
          const response = name.toLowerCase();
          setIsListening(false);

          if (response.includes('oui') || response.includes('ui') || response.includes('wi')) {
            // Confirmation positive
            playSound('success');
            speak(`Super ! Enchanté ${recognizedNameRef.current} !`);
            localStorage.setItem('childName', recognizedNameRef.current);
            setStep('confirmed');
            stepRef.current = 'confirmed';
            setTimeout(() => {
              navigate('/subjects');
            }, 3000);
          } else if (response.includes('non') || response.includes('nom')) {
            // Confirmation négative
            playSound('click');
            speak('D\'accord, redis-moi ton prénom !');
            setRecognizedName('');
            recognizedNameRef.current = '';
            setTimeout(() => {
              setStep('listening');
              stepRef.current = 'listening';
              setTimeout(() => {
                startListening();
              }, 2500);
            }, 2000);
          } else {
            // Réponse non reconnue
            speak('Je n\'ai pas compris. Dis oui ou non.');
            setTimeout(() => {
              startListening();
            }, 2500);
          }
        }
      };

      recog.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'no-speech') {
          speak('Je n\'ai rien entendu. Parle plus fort !');
          setTimeout(() => {
            startListening();
          }, 2000);
        }
      };

      recog.onend = () => {
        console.log('🔇 Recognition ended');
        setIsListening(false);
        isListeningRef.current = false;
      };

      recognitionRef.current = recog;
    } else {
      console.warn('⚠️ Speech Recognition not supported on this browser/device');
      console.log('Device info:', {
        userAgent: navigator.userAgent,
        isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent)
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleStartButton = () => {
    playSound('click');
    setStep('listening');
    stepRef.current = 'listening';

    // Parler et démarrer l'écoute
    speak('Bonjour ! Comment tu t\'appelles ? Dis-moi ton prénom.');
    setTimeout(() => {
      if (recognitionRef.current) {
        setIsListening(true);
        isListeningRef.current = true;
        try {
          recognitionRef.current.start();
        } catch (error) {
          console.error('❌ Error starting recognition:', error);
          setIsListening(false);
          isListeningRef.current = false;
        }
      }
    }, 4000);
  };

  const startListening = () => {
    console.log('🎤 startListening called, isListeningRef:', isListeningRef.current, 'recognition:', !!recognitionRef.current);
    if (recognitionRef.current && !isListeningRef.current) {
      setIsListening(true);
      isListeningRef.current = true;
      try {
        recognitionRef.current.start();
        console.log('✅ Recognition started by startListening');
      } catch (error) {
        console.error('❌ Error starting recognition:', error);
        setIsListening(false);
        isListeningRef.current = false;
      }
    } else {
      console.log('⚠️ Cannot start - already listening or no recognition, isListeningRef:', isListeningRef.current);
    }
  };

  const handleManualStart = () => {
    playSound('click');
    startListening();
  };

  const handleManualNameSubmit = () => {
    const name = recognizedName.trim();
    if (!name) return;

    playSound('click');
    setStep('confirming');
    stepRef.current = 'confirming';

    // Répéter le prénom entré et demander confirmation
    setTimeout(() => {
      speak(`J'ai entendu ${name}. C'est bien ça ? Dis oui ou non.`);
      setTimeout(() => {
        console.log('🔄 Attempting to restart listening for confirmation...');
        startListening();
      }, 3500);
    }, 500);
  };

  const handleYesClick = () => {
    // Confirmation positive
    playSound('success');
    speak(`Super ! Enchanté ${recognizedNameRef.current} !`);
    localStorage.setItem('childName', recognizedNameRef.current);
    setStep('confirmed');
    stepRef.current = 'confirmed';
    setTimeout(() => {
      navigate('/subjects');
    }, 3000);
  };

  const handleNoClick = () => {
    // Confirmation négative
    playSound('click');
    speak('D\'accord, redis-moi ton prénom !');
    setRecognizedName('');
    recognizedNameRef.current = '';
    setTimeout(() => {
      setStep('listening');
      stepRef.current = 'listening';
      setTimeout(() => {
        startListening();
      }, 2500);
    }, 2000);
  };

  return (
    <div>
      <div className="header">
        <h1 className="float">🎓 Mon Enfant 🎨</h1>
        <p>Dis-moi ton prénom !</p>
      </div>

      <div className="card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '5rem', marginBottom: '30px' }}>
          {step === 'initial' ? '👋' : step === 'listening' ? '🎤' : step === 'confirming' ? '❓' : '✅'}
        </div>

        {step === 'initial' && (
          <>
            <h2 style={{ fontSize: '2.5rem', color: '#667eea', marginBottom: '30px' }}>
              Bienvenue petit champion !
            </h2>
            <p style={{ fontSize: '1.5rem', marginBottom: '40px', color: '#666' }}>
              Clique pour commencer
            </p>
            <button
              className="btn btn-primary bounce"
              onClick={handleStartButton}
              style={{
                fontSize: '2rem',
                padding: '30px 60px'
              }}
            >
              🚀 Commencer
            </button>
          </>
        )}

        {step === 'listening' && (
          <>
            <h2 style={{ fontSize: '2rem', color: '#667eea', marginBottom: '30px' }}>
              Comment tu t'appelles ?
            </h2>
            {recognitionRef.current && (
              <p style={{ fontSize: '1.8rem', marginBottom: '40px', color: '#666', fontWeight: 'bold' }}>
                {isListening ? '🎤 Je t\'écoute...' : 'Dis-moi ton prénom !'}
              </p>
            )}

            <div style={{ marginTop: recognitionRef.current ? '40px' : '20px', borderTop: recognitionRef.current ? '2px dashed #ddd' : 'none', paddingTop: recognitionRef.current ? '40px' : '0' }}>
              <p style={{ fontSize: '1.2rem', marginBottom: '20px', color: '#999' }}>
                {recognitionRef.current ? 'Ou écris ton prénom ici :' : 'Écris ton prénom ici :'}
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={recognizedName}
                  onChange={(e) => {
                    setRecognizedName(e.target.value);
                    recognizedNameRef.current = e.target.value;
                  }}
                  placeholder="Ton prénom"
                  style={{
                    fontSize: '1.5rem',
                    padding: '15px 25px',
                    borderRadius: '10px',
                    border: '2px solid #667eea',
                    minWidth: '250px',
                    textAlign: 'center'
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && recognizedName.trim()) {
                      handleManualNameSubmit();
                    }
                  }}
                />
                <button
                  className="btn btn-success"
                  onClick={handleManualNameSubmit}
                  disabled={!recognizedName.trim()}
                  style={{
                    fontSize: '1.5rem',
                    padding: '15px 30px',
                    opacity: !recognizedName.trim() ? 0.5 : 1
                  }}
                >
                  ✅ Valider
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'confirming' && (
          <>
            <h2 style={{ fontSize: '2.5rem', color: '#667eea', marginBottom: '30px' }}>
              J'ai entendu : <span style={{ color: '#FF6B6B' }}>{recognizedName}</span>
            </h2>
            <p style={{ fontSize: '1.8rem', marginBottom: '40px', color: '#666', fontWeight: 'bold' }}>
              {isListening ? '🎤 Je t\'écoute...' : 'Dis oui ou non'}
            </p>

            <div style={{ marginTop: '40px', borderTop: '2px dashed #ddd', paddingTop: '40px' }}>
              <p style={{ fontSize: '1.2rem', marginBottom: '20px', color: '#999' }}>
                Ou clique ici :
              </p>
              <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-success bounce"
                  onClick={handleYesClick}
                  style={{
                    fontSize: '1.8rem',
                    padding: '20px 50px'
                  }}
                >
                  ✅ Oui
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleNoClick}
                  style={{
                    fontSize: '1.8rem',
                    padding: '20px 50px'
                  }}
                >
                  ❌ Non
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'confirmed' && (
          <>
            <h2 style={{ fontSize: '2.5rem', color: '#4CAF50', marginBottom: '30px' }} className="celebrate">
              🎉 Super {recognizedName} ! 🎉
            </h2>
            <p style={{ fontSize: '1.5rem', color: '#666' }}>
              C'est parti pour l'aventure !
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default NameInput;
